import { useState, useEffect } from 'react';
import { authApi, ApiError } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Loader2, FileText, ScrollText, Check } from 'lucide-react';
import { PrivacyPolicy } from './PrivacyPolicy';
import { TermsOfUse } from './TermsOfUse';

interface ConfirmSignupProps {
  username: string;
  onConfirmSuccess: () => void;
  onBackToLogin: () => void;
}

export const ConfirmSignup = ({ username: initialUsername, onConfirmSuccess, onBackToLogin }: ConfirmSignupProps) => {
  const [username, setUsername] = useState(initialUsername);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [policyScrolled, setPolicyScrolled] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [userIP, setUserIP] = useState('');

  // Get user's IP address
  useEffect(() => {
    const getUserIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIP(data.ip);
      } catch (error) {
        console.error('Error fetching IP address:', error);
        setUserIP('unknown');
      }
    };
    getUserIP();
  }, []);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username) {
      setError('Please enter your username');
      return;
    }

    if (!code) {
      setError('Please enter the confirmation code');
      return;
    }

    if (!policyScrolled || !termsScrolled) {
      setError('You must scroll to the bottom of both the Data Policy and Terms of Use to enable confirmation.');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.confirmSignup(username, code, userIP, new Date().toISOString());
      onConfirmSuccess();
    } catch (err) {
      const error = err as ApiError;
      setError(error.message || 'Confirmation failed. Please try again');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');

    if (!username) {
      setError('Please enter your username first');
      return;
    }

    setResendLoading(true);
    setResendSuccess(false);

    try {
      await authApi.resendCode(username);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      const error = err as ApiError;
      setError(error.message || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-white mb-2">Confirm Your Account</h2>
            <p className="text-white/80 text-sm">
              Enter the confirmation code sent to your email
            </p>
            {username && (
              <p className="text-blue-300 font-semibold mt-2">({username})</p>
            )}
            <p className="text-white/70 text-xs mt-3">
              Scroll through both the <strong>Data Policy</strong> and <strong>Terms of Use</strong> to enable confirmation.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-200 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {resendSuccess && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-400/50 rounded-lg text-green-200 text-sm">
              Confirmation code resent successfully!
            </div>
          )}

          <form onSubmit={handleConfirm} className="space-y-4">
            {!initialUsername && (
              <div>
                <Input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500"
                />
              </div>
            )}
            <div>
              <Input
                type="text"
                placeholder="Enter confirmation code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>

            {/* Privacy Policy and Terms of Use buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                onClick={() => { setShowPolicy(true); setPolicyScrolled(true); }}
                variant={policyScrolled ? "secondary" : "default"}
                className={`relative ${policyScrolled ? 'opacity-80' : ''}`}
              >
                <FileText className="w-4 h-4 mr-2" />
                Data Policy
                {policyScrolled && (
                  <Check className="w-4 h-4 ml-2 text-green-500" />
                )}
              </Button>

              <Button
                type="button"
                onClick={() => { setShowTerms(true); setTermsScrolled(true); }}
                variant={termsScrolled ? "secondary" : "default"}
                className={`relative ${termsScrolled ? 'opacity-80' : ''}`}
              >
                <ScrollText className="w-4 h-4 mr-2" />
                Terms of Use
                {termsScrolled && (
                  <Check className="w-4 h-4 ml-2 text-green-500" />
                )}
              </Button>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !policyScrolled || !termsScrolled}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-6 rounded-xl shadow-lg transition-all duration-200"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                'Confirm Account'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={handleResendCode}
              disabled={resendLoading || !policyScrolled || !termsScrolled}
              className="w-full text-white/90 hover:text-white hover:bg-white/10"
            >
              {resendLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resending...
                </>
              ) : (
                'Resend Code'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={onBackToLogin}
              className="w-full text-white/90 hover:text-white hover:bg-white/10"
            >
              Back to Login
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/60 text-xs">
              Didn't receive the code? Check your spam folder or resend it.
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicy
        open={showPolicy}
        onClose={() => setShowPolicy(false)}
        onScrollToEnd={() => setPolicyScrolled(true)}
      />

      {/* Terms of Use Modal */}
      <TermsOfUse
        open={showTerms}
        onClose={() => setShowTerms(false)}
        onScrollToEnd={() => setTermsScrolled(true)}
      />
    </>
  );
};
